// import external libraries
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';

// import data
import { queries } from 'RestAPIs/rapini';

// import utils
import {
  renderFilterOption,
  renderFilterTags
} from '../../BacklogFilters.utils';

// import utils
import { formatEntityDisplayLabel } from 'components/mrp/mrp.utils';

// import components
import { Autocomplete, TextField } from '@mui/material';

// import types
import type { AutocompleteRenderInputParams } from '@mui/material';
import type { PlanningEntityType, SchedulingLabel } from 'generated/index';
import type { BacklogEntityFilter } from '../../../../Backlog.types';
import type { BacklogEntitySearchProps } from './BacklogEntitySearch.types';

const SEARCH_LIMIT = 100;
// The backend only queries an entity source when the label set carries an internal or external label —
// with none it returns nothing. MRP is internal (like the shared picker's default), which reaches the
// SDB-planning entities the takenlijst works with.
const SCHEDULING_LABEL = 'MRP';

/**
 * One field that searches the tenant for a single entity type and lets the planner pick any number of
 * them. Unlike the shared afspraken-planning picker it is not always-open and holds only its own type, so
 * four of these sit cleanly in the backlog filter popover's grid. Each emits the full selection for its
 * type; the parent stitches the four back into one entityFilters list.
 */
const BacklogEntitySearch = ({
  entityType,
  label,
  selected,
  onChange,
  required
}: BacklogEntitySearchProps) => {
  'use memo';
  const { t } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounceValue(query, 300);

  const { data = [] } = queries.useFindPlanningEntities(
    debouncedQuery,
    // The endpoint takes a comma-separated string of types, not an array — one type here. The generated
    // client types it as an array, hence the cast.
    entityType as unknown as PlanningEntityType[],
    SEARCH_LIMIT,
    SCHEDULING_LABEL as unknown as SchedulingLabel[],
    { enabled: !!debouncedQuery }
  );

  const getOptionLabel = (option: BacklogEntityFilter) =>
    formatEntityDisplayLabel(option.label, option.number);

  const options: BacklogEntityFilter[] = data
    .filter((entity) => !!entity.id)
    .map((entity) => ({
      entityId: entity.id as string,
      planningEntityType: entityType,
      label: entity.label ?? '',
      number: entity.number
    }));

  return (
    <Autocomplete<BacklogEntityFilter, true>
      multiple
      // Several entities per field is the normal case, so the list stays open while ticking and every
      // option carries a checkbox.
      disableCloseOnSelect
      renderOption={renderFilterOption(getOptionLabel)}
      value={selected}
      options={options}
      // Named the way afsprakenplanning names an entity — "{number} - {name}" — so a planner recognises
      // the same care receiver or employee here as in the appointment views, and can tell two people
      // with the same name apart. Entity types without a number fall back to the plain name.
      getOptionLabel={getOptionLabel}
      // Results come and go from the server and the same entity can reappear across searches, so match
      // on the id rather than the default object identity — otherwise a picked entity is never seen as
      // already-selected.
      isOptionEqualToValue={(option, picked) =>
        option.entityId === picked.entityId
      }
      // One pick is named; several are summed into a count, as the activity field beside it does.
      renderTags={renderFilterTags(getOptionLabel, t('selected').toLowerCase())}
      // The server has already filtered to the query; keep every option it returned.
      filterOptions={(returned) => returned}
      noOptionsText={null}
      inputValue={query}
      onInputChange={(_event, value, reason) =>
        setQuery(reason === 'reset' ? '' : value)
      }
      onChange={(_event, value) => onChange(value)}
      // The boxed outlined field the flex match filter popper uses for its own dropdowns, so the four
      // entity fields read as the same control as the activity field beside them. fullWidth so each
      // fills its grid column rather than sizing to its content.
      fullWidth
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          required={required}
          error={required && selected.length === 0}
        />
      )}
    />
  );
};

export default BacklogEntitySearch;
